import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {Observable, of, Subject, timer, BehaviorSubject, from} from 'rxjs';
import {User} from 'src/app/models/user/user.model';
import {UserService} from 'src/app/services/api/user.service';
import {LogoutService} from 'src/app/services/web/logout.service';
import {StorageService} from 'src/app/services/web/storage.service';
import {environment} from '../../../../../environments/environment';
import {ReportViewerService} from '../../../../services/web/report-viewer.service';
import {BURGER_SVG_ICON} from '../../../../exports/constants';
import {MediprocityService} from '../../../../services/api/mediprocity.service';
import {concatMap, switchMap, takeUntil} from 'rxjs/operators';
import {NotificationService} from '../../../../services/web/notification.service';
import {confirm} from 'devextreme/ui/dialog';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})

export class TopbarComponent implements OnInit, OnDestroy {
  public icon = BURGER_SVG_ICON;
  public user$: Observable<User>;
  public showChangePassword = false;
  public showMFAmodal = false;
  public mfaModalClosed$ = new Subject<boolean>();
  public mfaModalOpened$ = new Subject<boolean>();
  public showSecondConfirmation = false;
  public showMediprocityLink = false;
  public showMediprocityWidget = false;
  public showMediprocityMailbox$: Subject<boolean>;
  public triggerWidgetReload = false;
  public userGuidePath = environment.userGuidePath;
  public topBarLogoPath = environment.topBarLogo;
  public unreadMessages: number;
  public advancedLicense = false;
  @Output() public showMobileNavigation = new EventEmitter();

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    private storageService: StorageService,
    private userService: UserService,
    private logoutService: LogoutService,
    private reporterViewer: ReportViewerService,
    private mediprocityService: MediprocityService,
    private notificationService: NotificationService
  ) {
  }

  public ngOnInit(): void {
    this.user$ = this.userService.getUserById(this.storageService.getSessionItem('userId'));
    this.advancedLicense = this.storageService.getSessionItem('advancedLicense') === 'true';

    const unreadMessage$ = timer(0, 30000).pipe(
      switchMap(_ => this.mediprocityService.getUnreadMessages())
    );

    this.showMediprocityMailbox$ = new BehaviorSubject<boolean>(false);
    this.showMediprocityMailbox$
      .pipe(
        switchMap(result => {
          if(result){
            return unreadMessage$;
          }
          return of(undefined);
        }),
        takeUntil(this.ngUnsubscribe)
      ).subscribe(unreadMessages => {
      this.unreadMessages = unreadMessages ? unreadMessages.unread_messages_count : 0;
    });
    this.showMailbox();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public logout(): void {
    this.logoutService.logout();
  }

  public modalClosed(): void {
    this.showChangePassword = false;
    this.showMediprocityLink = false;
    this.showMediprocityWidget = false;
    this.showMFAmodal = false;
    this.mfaModalClosed$.next(true);
    this.showSecondConfirmation = false;
    this.showMailbox();
  }

  public changePassword(): void {
    this.showChangePassword = true;
  }
  public get mfaEnabled(): boolean {
    return this.storageService.getSessionItem('mfaEnabled') === 'true';
  }
  public disableMFA(): void {
    this.userService.disableMFA()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result) => {
        if(!result){
          this.notificationService.success('MFA has been disabled', 'Success');
          this.storageService.removeSessionItem('mfaEnabled');
        }
      });
  }
  public openMFA(): void {
    this.showMFAmodal = true;
    this.mfaModalOpened$.next(true);
  }

  public disableMFAConfirmation(): void {
    const confirmation = from(confirm('Are you sure you want to disable Multi-Factor Authentication (MFA)?', 'Confirm'));

    confirmation
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(resp =>{
        if(resp){
          this.disableMFA();
          this.modalClosed();
        }
      });
  }

  public linkMediprocity(): void {
    this.showMediprocityLink = true;
  }

  public openMediprocity(): void {
    this.showMediprocityWidget = true;
    this.triggerWidgetReload = this.storageService.getSessionItem('mediReloadWidget') === 'true';
  }

  public userGuide(): void {
    this.reporterViewer.openWindow(this.userGuidePath);
  }

  private showMailbox(){
    if (this.advancedLicense && this.storedCredentials()) {
      this.showMediprocityMailbox$.next(true);
    }
    else{
      this.showMediprocityMailbox$.next(false);
    }
  }

  private storedCredentials(): boolean {
    const username = this.storageService.getSessionItem('mediUsername');
    const token = this.storageService.getSessionItem('mediAccessToken');
    return !!(username && token);
  }
}
